import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Volume2, VolumeX, Hand } from 'lucide-react';
import axios from 'axios';
import { ParkWiseLogo } from '../../components/ui/parkwise-logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIOSK_ID = "kiosk_001";
const SCREEN_ID = "kiosk_screen_001";
const SITE_ID = "site_kyle_001";

// Default configuration
const DEFAULT_CONFIG = {
  idleTimeoutSeconds: 60,          // Time before attract mode starts after inactivity
  advertRotationSeconds: 10,        // Time between advert changes
  touchToStartEnabled: true,        // Whether touch-to-start is enabled
  showLogo: true,                   // Show logo overlay on attract screen
  showClock: true,                  // Show clock on attract screen
  emergencyPriority: true           // Emergency messages override adverts
};

/**
 * Kiosk Attract Mode / Idle Screen
 * 
 * Features:
 * - Full-screen advert rotation from active campaigns
 * - Touch anywhere to return to home
 * - Emergency override support
 * - Impression logging
 * - Configurable rotation and idle timeouts
 */
export default function KioskAttract() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [adverts, setAdverts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEmergency, setIsEmergency] = useState(false);
  const [emergencyMessage, setEmergencyMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showTouchPrompt, setShowTouchPrompt] = useState(true);
  const videoRef = useRef(null);
  const rotationTimerRef = useRef(null);

  // Fetch adverts from playback API
  const fetchAdverts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/playback/screen/${SCREEN_ID}?site_id=${SITE_ID}`);
      const data = response.data;
      
      if (data.is_emergency && data.emergency_message) {
        setIsEmergency(true);
        setEmergencyMessage(data.emergency_message);
      } else {
        setIsEmergency(false);
        setEmergencyMessage('');
      }
      
      // Filter to only image/video content
      const mediaItems = (data.items || []).filter(item => 
        item.content_type === 'image' || item.content_type === 'video'
      );
      
      if (mediaItems.length > 0) {
        setAdverts(mediaItems);
      } else {
        // Fallback to default branding content if no adverts
        setAdverts([{
          content_id: 'default_branding',
          content_type: 'image',
          name: 'ParkWise Welcome',
          url: '/parkwise-logo.png',
          duration_seconds: 10
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch adverts:', error);
      // Set fallback content
      setAdverts([{
        content_id: 'fallback',
        content_type: 'image',
        name: 'ParkWise',
        url: '/parkwise-logo.png',
        duration_seconds: 10
      }]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Log impression when advert changes
  const logImpression = useCallback(async (contentId, contentName) => {
    try {
      await axios.post(`${API}/playback/${SCREEN_ID}/impression`, {
        content_id: contentId,
        content_name: contentName,
        site_id: SITE_ID,
        screen_id: SCREEN_ID,
        kiosk_id: KIOSK_ID,
        context: 'attract_mode'
      });
    } catch (error) {
      // Silently fail impression logging
      console.warn('Impression logging failed:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAdverts();
    
    // Refresh adverts periodically (every 5 minutes)
    const refreshInterval = setInterval(fetchAdverts, 5 * 60 * 1000);
    
    // Clock update
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    
    return () => {
      clearInterval(refreshInterval);
      clearInterval(clockInterval);
    };
  }, [fetchAdverts]);

  // Advert rotation
  useEffect(() => {
    if (adverts.length <= 1 || isEmergency) return;
    
    const currentAd = adverts[currentIndex];
    const duration = (currentAd?.duration_seconds || config.advertRotationSeconds) * 1000;
    
    rotationTimerRef.current = setTimeout(() => {
      const nextIndex = (currentIndex + 1) % adverts.length;
      setCurrentIndex(nextIndex);
      logImpression(adverts[nextIndex]?.content_id, adverts[nextIndex]?.name);
    }, duration);
    
    return () => {
      if (rotationTimerRef.current) {
        clearTimeout(rotationTimerRef.current);
      }
    };
  }, [currentIndex, adverts, config.advertRotationSeconds, isEmergency, logImpression]);

  // Log initial impression
  useEffect(() => {
    if (adverts.length > 0 && adverts[0]?.content_id) {
      logImpression(adverts[0].content_id, adverts[0].name);
    }
  }, [adverts, logImpression]);

  // Toggle touch prompt visibility
  useEffect(() => {
    const promptInterval = setInterval(() => {
      setShowTouchPrompt(prev => !prev);
    }, 3000);
    return () => clearInterval(promptInterval);
  }, []);

  // Handle touch/click to navigate to home
  const handleTouch = () => {
    navigate('/kiosk');
  };

  // Format time for display
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const currentAd = adverts[currentIndex];

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center bg-slate-950"
        onClick={handleTouch}
      >
        <div className="text-center">
          <ParkWiseLogo size="xl" className="mb-8" />
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Emergency override display
  if (isEmergency && emergencyMessage) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center bg-red-600 p-8 cursor-pointer"
        onClick={handleTouch}
        data-testid="kiosk-emergency-override"
      >
        <AlertTriangle className="w-24 h-24 text-white mb-8 animate-pulse" />
        <h1 className="text-4xl md:text-6xl font-bold text-white text-center mb-6">
          Important Notice
        </h1>
        <p className="text-2xl md:text-3xl text-white/90 text-center max-w-4xl leading-relaxed">
          {emergencyMessage}
        </p>
        <div className="mt-12 flex items-center gap-3 text-white/70">
          <Hand className="w-6 h-6" />
          <span className="text-lg">Touch screen to continue</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative overflow-hidden cursor-pointer bg-slate-950"
      onClick={handleTouch}
      data-testid="kiosk-attract-mode"
    >
      {/* Full-screen advert content */}
      <div className="absolute inset-0">
        {currentAd?.content_type === 'video' ? (
          <video
            ref={videoRef}
            src={currentAd.media_url || currentAd.url}
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted={isMuted}
            playsInline
            data-testid="attract-video"
          />
        ) : (
          <div 
            className="w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: (currentAd?.media_url || currentAd?.url) ? `url(${currentAd.media_url || currentAd.url})` : 'none',
              backgroundColor: '#0f172a'
            }}
            data-testid="attract-image"
          >
            {/* Fallback branding content */}
            {(!(currentAd?.media_url || currentAd?.url) || currentAd.content_id === 'fallback' || currentAd.content_id === 'default_branding') && (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <ParkWiseLogo size="kiosk" />
                <p className="mt-8 text-2xl text-slate-400">
                  Welcome to Smart Parking
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40" />

      {/* Header with logo and time */}
      {config.showLogo && (
        <header className="absolute top-0 left-0 right-0 p-6 md:p-8 flex items-center justify-between z-10">
          <ParkWiseLogo size="lg" />
          
          {config.showClock && (
            <div className="text-right">
              <p className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {formatTime(currentTime)}
              </p>
              <p className="text-lg text-white/70">
                {formatDate(currentTime)}
              </p>
            </div>
          )}
        </header>
      )}

      {/* Touch to Start prompt */}
      <div 
        className={`absolute bottom-0 left-0 right-0 pb-12 flex flex-col items-center justify-center z-10 transition-opacity duration-1000 ${showTouchPrompt ? 'opacity-100' : 'opacity-50'}`}
      >
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center animate-pulse">
            <Hand className="w-8 h-8 text-white" />
          </div>
        </div>
        <p className="text-2xl md:text-3xl font-semibold text-white text-center">
          Touch screen to start
        </p>
        <p className="text-lg text-white/60 mt-2">
          Register • Pay • Check Status
        </p>
      </div>

      {/* Advert indicators (dots) */}
      {adverts.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {adverts.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                idx === currentIndex ? 'bg-white w-6' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      )}

      {/* Video mute toggle (if video content) */}
      {currentAd?.content_type === 'video' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMuted(!isMuted);
          }}
          className="absolute bottom-6 right-6 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors z-20"
          data-testid="mute-toggle"
        >
          {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
        </button>
      )}

      {/* Current ad info (for debugging/admin) */}
      {process.env.NODE_ENV === 'development' && currentAd && (
        <div className="absolute top-4 right-4 bg-black/70 text-white text-xs px-3 py-2 rounded z-20">
          Ad: {currentAd.name || currentAd.content_id}
        </div>
      )}
    </div>
  );
}
